<template>
  <div class="limiter">
    <div class="container-login100" style="background-image: url('/assets/img/agency-login-back.jpg');">
      <div class="wrap-login100">
        <div class="w-full" v-if="isOdysseySite"> <!-- Agent login part -->
          <div class="login100-form-avatar mb-5">
            <img :src="srcLogo" alt="logo">
          </div>

          <div class="wrap-input100 mb-3">
            <b-form-input type="text" class="input100" :state="!!odyAgentCode" placeholder="Odyssey Agent Code" v-model="odyAgentCode"></b-form-input>
            <span class="symbol-input100">
              <i class="fa fa-id-card"></i>
            </span>
          </div>
          <div class="wrap-input100 mb-3">
            <b-form-input type="text" class="input100" :state="!!clerkName" placeholder="Clerk Name" v-model="clerkName"></b-form-input>
            <span class="symbol-input100">
              <i class="fa fa-user"></i>
            </span>
          </div>
          <div class="wrap-input100 mb-3">
            <b-form-input
              type="password"
              class="input100"
              :state="!!password"
              placeholder="Password"
              v-model="password"
              @keypress.enter="() => requestAgentLogin()"
            ></b-form-input>
            <span class="symbol-input100">
              <i class="fa fa-lock"></i>
            </span>
          </div>

          <div class="container-login100-form-btn mt-3">
            <b-button class="login100-form-btn" @click="requestAgentLogin">
              {{$t("login.odyssey-login")}}
            </b-button>
          </div>
        </div>

        <div class="w-full" v-else-if="isLoginMarketer"> <!-- Marketer login part -->
          <div class="login100-form-avatar mb-5">
            <img :src="srcLogo" :alt="agencyName">
          </div>

          <span class="login100-form-title pt-20 pb-45 mb-3">
            {{ agencyName }}
          </span>

          <!-- for 1FA and pass login marketer -->
          <div class="mb-3" v-if="!is2FA && !preferSmsToPass">
            <div class="text-center w-full guide-caption">
              {{ $t("login.enter-marketer-password") }}
            </div>

            <div class="wrap-input100 mb-3">
              <b-form-input
                type="password"
                class="input100"
                :state="!!marketerPassword"
                placeholder="Password"
                v-model="marketerPassword"
                @keypress.enter="() => requestMarketerPassLogin()"
              ></b-form-input>
              <span class="symbol-input100">
                <i class="fa fa-lock"></i>
              </span>
            </div>

            <div class="container-login100-form-btn">
              <b-button class="login100-form-btn" @click="requestMarketerPassLogin">
                {{$t("login.agency-login")}}
              </b-button>
            </div>
          </div>

          <!-- Step 1: Request Sms login -->
          <div class="mb-3" v-else-if="!agencySid">
            <div class="w-full" v-if="is2FA">
              <div class="text-center w-full mb-4 guide-caption">
                {{ $t("login.enter-passcode") }}
              </div>

              <div class="wrap-input100 mb-3">
                <b-form-input
                  type="text"
                  class="input100"
                  :state="!!passCode"
                  placeholder="Passcode"
                  v-model="passCode"
                  @keypress.enter="() => requestMarketerSmsLogin()"
                ></b-form-input>
                <span class="symbol-input100">
                  <i class="fa fa-id-card"></i>
                </span>
              </div>
            </div>

            <div class="container-login100-form-btn">
              <b-button class="login100-form-btn" @click="requestMarketerSmsLogin">
                {{$t("login.agency-login")}}
              </b-button>
            </div>
          </div>

          <!-- Step 2: Request Verify Code -->
          <div class="mb-3" v-else>
            <div class="text-center w-full mb-4 guide-caption">
              {{ $t("login.enter-sms-code") }}
            </div>
            <div class="text-center w-full mb-3 sms-notify-caption" v-if="remainingTime > 0">
              {{ $t("login.sms-verify-message-with-time", { time: formattedRemainingTime }) }}
            </div>
            <div class="text-center w-full mb-3 flex flex-col" v-else>
              <p class="expired-message mb-2">{{ $t("login.sms-verify-expired-message") }}</p>
              <b-button class="resend-button" @click="requestMarketerSmsLogin">
                {{ $t("login.resend-passcode") }}
              </b-button>
            </div>
            <div class="wrap-input100 mb-3">
              <b-form-input
                type="text"
                class="input100"
                :state="!!verifyCode"
                placeholder="Verify Code"
                v-model="verifyCode"
                @keypress.enter="() => requestVerifyCode()"
              ></b-form-input>
              <span class="symbol-input100">
                <i class="fa fa-id-card"></i>
              </span>
            </div>

            <div class="container-login100-form-btn">
              <b-button class="login100-form-btn" @click="requestVerifyCode">
                {{$t("login.agency-verify-sms")}}
              </b-button>
            </div>
          </div>
        </div>

        <div class="w-full">
          <!-- Cancel Button -->
          <div class="container-login100-form-btn mt-3">
            <b-button class="login100-form-btn" @click="cancelLogin">
              {{$t("login.cancel")}}
            </b-button>
          </div>

          <!-- error message -->
          <div class="text-center w-full error-message" v-if="errorMessage">
            {{ $t("login.login-error-message") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { BButton, BFormInput } from 'bootstrap-vue';

export default {
  name: 'agency-agent-login',
  components: {
    BButton, BFormInput,
  },
  data() {
    return {
      odyAgentCode: '',         // Odyssey Agent Code
      clerkName: '',            // Clerk Name
      password: '',             // agent Password
      marketerPassword: '',     // marketer Password
      passCode: '',             // Passcode (pax id)
      verifyCode: '',           // Verify Code
      remainingTime: 0,         // Time left for verify code
    };
  },
  mounted() {
    // Start countdown for remaining time
    this.startCountdown();
  },
  computed: {
    ...mapGetters({
      isAuthorized: 'IS_AGENCY_AUTHORIZED',
      whiteLabel: 'GET_WHITE_LABEL_DATA',
      marketerAgencyContent: 'GET_MARKETER_AGENCY_CONTENT',
      errorMessage: 'GET_AGENCY_ERROR_MESSAGE',
      agencySid: 'GET_AGENCY_SID',
      isOdysseySite: 'GET_ODYSSEY_AGENT_STATE',
      odysseyAgentContent: 'GET_ODYSSEY_AGENT_CONTENT',
      isLoginMarketer: 'GET_IS_LOGIN_MARKETER',
      is2FA: 'GET_IS_2FA',
      preferSmsToPass: 'GET_PREFER_SMS_PASS',
    }),
    srcLogo() {
      const { isOdysseySite, odysseyAgentContent, marketerAgencyContent,  whiteLabel } = this;
      if (isOdysseySite) {
        return odysseyAgentContent.logoUrls?.[0] || '';
      }

      return marketerAgencyContent?.logoUrls?.[0] || whiteLabel.logoUrls?.[0] || '';
    },
    agencyName() {
      const { marketerAgencyContent } = this;
      return (marketerAgencyContent === null) ? this.whiteLabel.name_Loc.content : marketerAgencyContent.name_Loc.content;
    },
    formattedRemainingTime() {
      const minutes = Math.floor(this.remainingTime / 60);
      const seconds = this.remainingTime % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
  },
  watch: {
    verifyCode() {
      this.$store.commit('SET_AGENCY_VERIFY_CODE', this.verifyCode);
    },
    agencySid() {
      this.verifyCode = '';
    },
  },
  methods: {
    async requestAgentLogin() {
      if (this.isOdysseySite && (this.clerkName && this.password && this.odyAgentCode)) {
        await this.$store.dispatch('REQUEST_AGENCY_LOGIN', { odyAgentCode: this.odyAgentCode, clerkName: this.clerkName, password: this.password });
      }
    },
    async requestMarketerPassLogin() {
      await this.$store.dispatch('REQUEST_MARKETER_PASS_LOGIN', { password: this.marketerPassword });
    },
    async requestMarketerSmsLogin() {
      this.remainingTime = await this.$store.dispatch('REQUEST_MARKETER_SMS_LOGIN', { passCode: this.passCode });
      if( this.remainingTime > 0) {
        this.startCountdown();
      }
    },
    async requestVerifyCode() {
      await this.$store.dispatch('REQUEST_AGENCY_BY_VERIFY_CODE');
    },
    async cancelLogin() {
      if (this.isAuthorized) {
        await this.$store.dispatch('REQUEST_AGENCY_LOGOUT');
      }

      if (this.isOdysseySite) {
        window.location.href = process.env.VUE_APP_MAIN_DOMAIN;
      } else {
        window.location = '/';
      }
    },
    startCountdown() {
      const interval = setInterval(() => {
        if (this.remainingTime > 0) {
          this.remainingTime--;
        } else {
          clearInterval(interval);
        }
      }, 1000);
    },
  },
};
</script>

<style scoped>
a {
  font-size: 14px;
  line-height: 1.7;
  color: #666666;
  margin: 0px;
  transition: all 0.4s;
  -webkit-transition: all 0.4s;
  -o-transition: all 0.4s;
  -moz-transition: all 0.4s;
}

a:focus {
  outline: none !important;
}

a:hover {
  text-decoration: none;
  color: #fff;
}

/*---------------------------------------------*/
h1,h2,h3,h4,h5,h6 {
  margin: 0px;
}

p {
  font-size: 14px;
  line-height: 1.7;
  color: #666666;
  margin: 0px;
}

ul, li {
  margin: 0px;
  list-style-type: none;
}

/*---------------------------------------------*/
input {
  outline: none;
  border: none;
}

textarea {
  outline: none;
  border: none;
}

textarea:focus, input:focus {
  border-color: transparent !important;
}

input:focus::-webkit-input-placeholder { color:transparent; }
input:focus:-moz-placeholder { color:transparent; }
input:focus::-moz-placeholder { color:transparent; }
input:focus:-ms-input-placeholder { color:transparent; }

textarea:focus::-webkit-input-placeholder { color:transparent; }
textarea:focus:-moz-placeholder { color:transparent; }
textarea:focus::-moz-placeholder { color:transparent; }
textarea:focus:-ms-input-placeholder { color:transparent; }

input::-webkit-input-placeholder { color: #999999; }
input:-moz-placeholder { color: #999999; }
input::-moz-placeholder { color: #999999; }
input:-ms-input-placeholder { color: #999999; }

textarea::-webkit-input-placeholder { color: #999999; }
textarea:-moz-placeholder { color: #999999; }
textarea::-moz-placeholder { color: #999999; }
textarea:-ms-input-placeholder { color: #999999; }

/*---------------------------------------------*/
button {
  outline: none !important;
  border: none;
  background: transparent;
}

button:hover {
  cursor: pointer;
}

iframe {
  border: none !important;
}

/*//////////////////////////////////////////////////////////////////
[ Utility ]*/
.txt1 {
  font-size: 16px;
  color: #cccccc;
  line-height: 1.4;
}
.sms-notify-caption {
  color: #d5d81f;
  text-shadow: 1px 1px black;
  font-size: 1.2rem;
}
.guide-caption {
  color: #bffb75;
  text-shadow: 1px 1px black;
  font-size: 1.5rem;
}
.error-message {
  color: #fb7575;
  text-shadow: 1px 1px black;
  font-size: 1.5rem;
}
.expired-message {
  color: #ff0000;
  font-size: 1.2rem;
  font-weight: 400;
  text-shadow: 1px 1px black;
}
.resend-button {
  background-color: #005bea;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}
.resend-button:hover {
  background-color: #003c8f;
}

.bo1 {
  border-bottom: 1px solid #999999;
}

.hov1:hover {
  border-color: #d33f8d;
}

/*//////////////////////////////////////////////////////////////////
[ login ]*/

.limiter {
  width: 100%;
  margin: 0 auto;
}

.container-login100 {
  width: 100%;
  min-height: 100vh;
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  padding: 15px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  position: relative;
  z-index: 1;
}

.container-login100::before {
  content: "";
  display: block;
  position: absolute;
  z-index: -1;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: #005bea;
  background: -webkit-linear-gradient(to bottom, #005bea, #00c6fb);
  background: -o-linear-gradient(to bottom, #005bea, #00c6fb);
  background: -moz-linear-gradient(to bottom, #005bea, #00c6fb);
  background: linear-gradient(to bottom, #005bea, #00c6fb);
  opacity: 0.9;
}

.wrap-login100 {
  width: 390px;
  background: transparent;
}

/*------------------------------------------------------------------
[  ]*/
.login100-form {
  width: 100%;
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.login100-form-title {
  font-size: 24px;
  color: #fff;
  line-height: 1.2;
  text-align: center;

  width: 100%;
  display: block;
}

/*---------------------------------------------*/
.login100-form-avatar {
  max-width: 80%;
  overflow: hidden;
  margin: 0 auto;
}
.login100-form-avatar img {
  width: 100%;
  height: 100%;
}

/*---------------------------------------------*/
.wrap-input100 {
  position: relative;
  width: 100%;
  z-index: 1;
}

.input100 {
  font-size: 15px;
  line-height: 1.2;
  color: #333333;

  display: block;
  width: 100%;
  height: 50px;
  border-radius: 25px;
  padding: 0 53px 0 53px !important;
  border-style: solid;
  border-width: 2px;
  background-position: left calc(0.375em + 0.5rem) center;
}

/*------------------------------------------------------------------
[ Focus ]*/
.focus-input100 {
  display: block;
  position: absolute;
  border-radius: 25px;
  bottom: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  box-shadow: 0px 0px 0px 0px;
  color: rgba(0,91,234, 0.6);
}

.input100:focus + .focus-input100 {
  -webkit-animation: anim-shadow 0.5s ease-in-out forwards;
  animation: anim-shadow 0.5s ease-in-out forwards;
}

@-webkit-keyframes anim-shadow {
  to {
    box-shadow: 0px 0px 80px 30px;
    opacity: 0;
  }
}

@keyframes anim-shadow {
  to {
    box-shadow: 0px 0px 80px 30px;
    opacity: 0;
  }
}

.symbol-input100 {
  font-size: 15px;
  color: #999999;

  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  align-items: center;
  position: absolute;
  border-radius: 25px;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-right: 30px;
  pointer-events: none;

  -webkit-transition: all 0.4s;
  -o-transition: all 0.4s;
  -moz-transition: all 0.4s;
  transition: all 0.4s;
}

.input100:focus + .focus-input100 + .symbol-input100 {
  color: #00c6fb;
  padding-right: 23px;
}

/*------------------------------------------------------------------
[ Button ]*/
.container-login100-form-btn {
  width: 100%;
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.login100-form-btn {
  font-size: 15px;
  line-height: 1.5;
  color: #e0e0e0;

  width: 100%;
  height: 50px;
  border-radius: 25px;
  background: #333333;
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 25px;

  -webkit-transition: all 0.4s;
  -o-transition: all 0.4s;
  -moz-transition: all 0.4s;
  transition: all 0.4s;

  position: relative;
  z-index: 1;
}

.login100-form-btn::before {
  content: "";
  display: block;
  position: absolute;
  z-index: -1;
  width: 100%;
  height: 100%;
  border-radius: 25px;
  top: 0;
  left: 0;
  background: #005bea;
  background: -webkit-linear-gradient(to left, #005bea, #00c6fb);
  background: -o-linear-gradient(to left, #005bea, #00c6fb);
  background: -moz-linear-gradient(to left, #005bea, #00c6fb);
  background: linear-gradient(to left, #005bea, #00c6fb);
  -webkit-transition: all 0.4s;
  -o-transition: all 0.4s;
  -moz-transition: all 0.4s;
  transition: all 0.4s;
  opacity: 0;
}

.login100-form-btn:hover {
  background: transparent;
  color: #fff;
}

.login100-form-btn:hover:before {
  opacity: 1;
}

/*------------------------------------------------------------------
[ Button sign in with ]*/
.btn-face,
.btn-google {
  font-size: 16px;
  line-height: 1.2;

  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc((100% - 10px) / 2);
  height: 40px;
  border-radius: 3px;
  border: 1px solid #e6e6e6;
  background-color: #fff;

  -webkit-transition: all 0.4s;
  -o-transition: all 0.4s;
  -moz-transition: all 0.4s;
  transition: all 0.4s;
}

.btn-face {
  color: #3b5998;
}

.btn-face i {
  font-size: 20px;
  margin-right: 10px;
  padding-bottom: 1px;
}

.btn-google {
  color: #555555;
}

.btn-google img {
  width: 19px;
  margin-right: 10px;
  padding-bottom: 1px;
}

.btn-face:hover,
.btn-google:hover {
  border-color: #d33f8d;
}

@media (max-width: 576px) {
  .wrap-login100 {
    padding-top: 80px;
    padding-left: 15px;
    padding-right: 15px;
  }
}
</style>
